import { useRef, useState } from 'react';
import { generatePdf } from '@/utils/pdf-generation.utils';
import PdfProgressModal from '@/components/Custom/Modal/PdfProgressModal';
import { showModal, hideModal } from '@/utils/modal.utils';

interface DownloadPdfProps {
  filename: string;
}

export const useDownloadPdf = () => {
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [currentSize, setCurrentSize] = useState(0);

  const cleanupHtml2PdfElements = () => {
    const overlay = document.querySelector('.html2pdf__overlay');
    const container = document.querySelector('.html2canvas-container');

    if (overlay) {
      overlay.remove();
    }
    if (container) {
      container.remove();
    }
  };

  const cancelPdfGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsGeneratingPdf(false);
      hideModal('#pdf-progress-modal');
      cleanupHtml2PdfElements();
    }
  };

  const downloadPdf = async ({ filename }: DownloadPdfProps) => {
    setIsGeneratingPdf(true);
    showModal('#pdf-progress-modal');

    setProgress(0);
    setTotalSize(0);
    setCurrentSize(0);

    // Create new AbortController
    abortControllerRef.current = new AbortController();

    // Wrap setTimeout in a Promise to properly handle async/await
    await new Promise<void>((resolve) => {
      setTimeout(async () => {
        try {
          await generatePdf({
            pdfRef,
            filename,
            abortController: abortControllerRef.current,
            onProgress: (progress, totalSize, currentSize) => {
              setProgress(progress);
              setTotalSize(totalSize);
              setCurrentSize(currentSize);
            },
          });
          resolve();
        } catch (error) {
          // Silently handle cancellation errors, but log other errors
          if (
            !(
              error instanceof Error &&
              error.message === 'PDF generation cancelled'
            )
          ) {
            console.error('Error generating PDF:', error);
          }
          resolve();
        } finally {
          setIsGeneratingPdf(false);
          abortControllerRef.current = null;
          hideModal('#pdf-progress-modal');
        }
      }, 0);
    });
  };

  return {
    downloadPdf,
    isGeneratingPdf,
    renderPdfModel: (children: React.ReactNode) => (
      <>
        {isGeneratingPdf && (
          <div className="hidden">
            <div ref={pdfRef}>{children}</div>
          </div>
        )}
        <PdfProgressModal
          id="pdf-progress-modal"
          progress={progress}
          totalSize={totalSize}
          currentSize={currentSize}
          onClose={cancelPdfGeneration}
        />
      </>
    ),
  };
};
